import {
  FactoryRounded,
  FeedRounded,
  KeyboardRounded,
  PendingRounded,
  PeopleRounded,
} from "@mui/icons-material";
import { Box, Button, Typography, useTheme } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAuthStore, usePortalStore } from "../../stores/store";
import { AuthenStoreType } from "../../types/authen";
import { InitialUserPortalType } from "../../types/global";

const Form = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const setPortal = usePortalStore(
    (state) => (state as InitialUserPortalType).setPortalState,
  );

  const { user: auth } = useAuthStore((state) => state as AuthenStoreType);

  const hasCrmOrReward =
    auth?.duties?.includes("crm") || auth?.duties?.includes("reward");

  const firstDuty = hasCrmOrReward
    ? auth?.duties?.find((d) => !["crm", "reward"].includes(d)) ?? "crm"
    : auth?.duties[0] ?? "task";

  const [selectedPortal, setSelectedPortal] = useState(
    hasCrmOrReward ? "crm" : firstDuty,
  );

  const portalOptions = [
    {
      label: "Task Creator",
      value: "task",
      icon: <FeedRounded />,
    },
    {
      label: "Key-In",
      value: "keyin",
      icon: <KeyboardRounded />,
    },
    {
      label: "Support",
      value: "support",
      icon: <PendingRounded />,
    },
    {
      label: "To-QC/Answer",
      value: "qc",
      icon: <FactoryRounded />,
    },
    {
      label: "CRM",
      value: "crm",
      icon: <PeopleRounded />,
    },
  ];

  const onSubmit = () => {
    switch (selectedPortal) {
      case "task":
        navigate("/user/task-creator", { replace: true });
        break;
      case "keyin":
        navigate("/user/keyin", { replace: true });
        break;
      case "support":
        navigate("/user/support", { replace: true });
        break;
      case "qc":
        navigate("/user/qc", { replace: true });
        break;
      case "crm":
        navigate("/crm", { replace: true });
        break;
      default:
        break;
    }
    setPortal(selectedPortal.toUpperCase() ?? "");
  };
  return (
    <Box className="w-full max-w-[392px] p-4">
      <Typography variant="body1" className="text-xl font-bold mb-2">
        Log in to the Essilor system
      </Typography>
      <div className="flex gap-2 items-center mb-4">
        <div className="text-sm text-gray-400">
          If you need more info, please check out
        </div>
        <div className="text-sm text-info-dark">FAQ Page</div>
      </div>
      <FormControl className="mb-4 w-full"> 
        <RadioGroup
          row
          aria-labelledby="demo-form-control-label-placement"
          name="position"
          defaultValue="TASK_CREATOR"
          className="gap-3"
          value={selectedPortal}
          onChange={(e) => setSelectedPortal(e.target.value)}
        >
          {portalOptions?.map((portal, index) => {
            const isVisible =
              portal.value === "crm"
                ? hasCrmOrReward
                : auth?.duties.includes(portal.value);
            if (isVisible) {
              return (
                <FormControlLabel
                  key={`portal-${index}`}
                  value={portal.value}
                  control={<Radio />}
                  label={
                    <div className="flex items-center gap-2 ">
                      {portal.icon}
                      <div>{portal.label}</div>
                    </div>
                  }
                  labelPlacement="start"
                  className="w-full justify-between px-3 py-4 rounded m-0"
                  sx={[
                    {
                      position: "relative",
                      border: `1px solid ${theme.vars.palette.grey[200]}`,
                      "&:hover, &:active": {
                        boxShadow: `inset 0 0 0 1px ${theme.vars.palette.primary.main}`,
                        border: `1px solid ${theme.vars.palette.primary.main}`,
                      },
                    },
                    selectedPortal === portal.value && {
                      border: `1px solid ${theme.vars.palette.primary.main}`,
                      boxShadow: `inset 0 0 0 1px ${theme.vars.palette.primary.main}`,
                    },
                  ]}
                />
              );
            }
          })}
        </RadioGroup>
      </FormControl>
      <div className="flex md:justify-end">
        <Button
          variant="contained"
          className="w-full md:w-full md:max-w-[120px]"
          onClick={() => onSubmit()}
        >
          Confirm
        </Button>
      </div>
    </Box>
  );
};

export default Form;
